import Sortable from "sortablejs";
import { Toast } from "../../utils/Toast";

interface SortableListOptions {
    handle?: string;
    /** SortableJS `cancel` 옵션 — 이 셀렉터에 매칭되는 요소에서 시작하는 드래그는 무시 */
    cancel?: string;
    onSaved?: () => void;
    onError?: (msg: string) => void;
    csrf?: string;
    reloadOnSave?: boolean;
}

const DEFAULT_CANCEL = "input, textarea, button, a, label, select, [data-bulk-row], [data-no-drag]";

/**
 * Wraps Sortable.js for admin list reordering.
 * Usage:
 *   new SortableList(tbodyEl, "/console/site/works/reorder", csrfToken).init();
 *
 * RESTful: PATCH /resources/reorder  body: { items: [{id, sort_order},...] }
 * Expects each draggable row to have data-id attribute.
 */
export class SortableList {
    readonly #el: HTMLElement;
    readonly #endpoint: string;
    readonly #opts: Required<SortableListOptions>;
    #sortable: Sortable | null = null;
    static readonly #GLOBAL_LOADER_ID = "admin-fullscreen-loader";

    constructor(el: HTMLElement, endpoint: string, opts: SortableListOptions = {}) {
        this.#el = el;
        this.#endpoint = endpoint;
        this.#opts = {
            handle: opts.handle ?? "",
            cancel: opts.cancel ?? DEFAULT_CANCEL,
            onSaved: opts.onSaved ?? (() => {}),
            onError: opts.onError ?? (() => {}),
            csrf: opts.csrf ?? this.#readCsrf(),
            reloadOnSave: opts.reloadOnSave ?? false,
        };
    }

    #readCsrf(): string {
        return (
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
            document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ??
            ""
        );
    }

    init(): this {
        const cfg: Sortable.Options = {
            animation: 150,
            ghostClass: "admin-sortable__ghost",
            dragClass: "admin-sortable__drag",
            draggable: "tr",
            onEnd: () => void this.#save(),
        };
        if (this.#opts.handle) cfg.handle = this.#opts.handle;
        if (this.#opts.cancel) cfg.filter = this.#opts.cancel;

        this.#sortable = Sortable.create(this.#el, cfg);
        return this;
    }

    destroy(): void {
        this.#sortable?.destroy();
        this.#sortable = null;
    }

    async #save(): Promise<void> {
        const rows = Array.from(this.#el.querySelectorAll<HTMLElement>(":scope > tr[data-id]"));
        const items = rows.map((row, idx) => ({
            id: parseInt(row.dataset.id ?? "0", 10),
            sort_order: (idx + 1) * 10,
        }));

        this.#setLoader(true);
        this.#setError(false);

        try {
            const res = await fetch(this.#endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-TOKEN": this.#opts.csrf,
                },
                body: JSON.stringify({ items }),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`HTTP ${res.status}${text ? ": " + text.slice(0, 120) : ""}`);
            }

            const json = (await res.json()) as { result?: string; msg?: string };
            if (json.result === "success") {
                this.#opts.onSaved();
                if (this.#opts.reloadOnSave) {
                    window.location.reload();
                } else {
                    this.#setLoader(false);
                    Toast.success("순서가 저장되었습니다.");
                }
            } else {
                throw new Error(json.msg ?? "순서 저장 실패");
            }
        } catch (e) {
            this.#setLoader(false);
            const msg = e instanceof Error ? e.message : "순서 저장 중 오류가 발생했습니다.";
            this.#setError(true, msg);
            this.#opts.onError(msg);
        }
    }

    #getWrap(): Element | null {
        return this.#el.closest("[data-sortable-wrap]");
    }

    #setLoader(visible: boolean): void {
        const loader = this.#ensureGlobalLoader();
        loader.classList.toggle("admin-sortable__saving--visible", visible);
        document.documentElement.classList.toggle("admin--loading", visible);
    }

    #ensureGlobalLoader(): HTMLElement {
        let el = document.getElementById(SortableList.#GLOBAL_LOADER_ID) as HTMLElement | null;
        if (el) return el;

        el = document.createElement("div");
        el.id = SortableList.#GLOBAL_LOADER_ID;
        el.className = "admin-sortable__saving";
        el.dataset.sortableLoader = "";
        el.setAttribute("aria-live", "polite");
        el.setAttribute("aria-label", "순서 저장 중");
        el.innerHTML = `
          <div class="admin-sortable__saving-inner">
            <i class="fa-solid fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
            <span>순서 저장 중...</span>
          </div>
        `;
        document.body.appendChild(el);
        return el;
    }

    #setError(visible: boolean, msg = ""): void {
        const wrap = this.#getWrap();
        if (!wrap) return;

        let el = wrap.querySelector<HTMLElement>("[data-sortable-error]");
        if (!el) {
            el = document.createElement("div");
            el.className = "admin-sortable__error";
            el.dataset.sortableError = "";
            el.innerHTML = '<i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i><span></span>';
            wrap.prepend(el);
        }

        const span = el.querySelector("span");
        if (span) span.textContent = msg ? `순서 저장 실패: ${msg}` : "";
        el.classList.toggle("admin-sortable__error--visible", visible);
    }

}
